import { useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Stack,
  FormControl,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  CircularProgress,
  Grid
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { ReactComponent as AddIcon } from '../../../public/static/dashboard/AddIcon.svg';
import {
  ConfirmDialog,
  DateRangePickerDialog,
  NoDataAvailable
} from '../../components/_dashboard/app';
import { CustomPagination, SnackBarAlert } from '../../components/HelperComponents';
import { useAuth } from '../../components/customContextProviders/AuthContext';
import { isoDateFormatDmyhs } from '../../utils/formatTime';
import {
  cleanTicketDescriptionText,
  getTicketRowChipStyle,
  rowFreshdeskDisplayId,
  ticketVpaForExpanded
} from '../utils/ticketManagementUtils';
import { useTicketManagement } from '../hooks/useTicketManagement';
import AddTicketModal from './AddTicketModal';
import TicketsMoreMenu from './TicketsMoreMenu';

/**
 * Merchant search UI: explicit type → submit search → pick from Select when the hook swaps to
 * picker mode (`merchantTicketShowMerchantPicker`). PR-suggested Autocomplete, async searchable
 * select, or debounced lookup would change hook timing/API usage; kept out of this module so
 * behavior and layout stay aligned with `useTicketManagement`.
 */

const ticketStatsConfig = [
  { label: 'Total', keys: ['Total', 'total'], color: '#054EEE' },
  { label: 'Open', keys: ['Open', 'open'], color: '#b90909d6' },
  { label: 'Pending', keys: ['Pending', 'pending'], color: '#ff9900e0' },
  { label: 'Resolved', keys: ['Resolved', 'resolved'], color: '#045e07ce' },
  { label: 'Closed', keys: ['Closed', 'closed'], color: '#9e9e9ecb' },
  {
    label: 'Waiting On Branch',
    keys: ['Waiting On Branch', 'waitingOnBranch'],
    color: '#1976d2'
  },
  {
    label: 'Waiting On Head Office',
    keys: ['Waiting On Head Office', 'waitingOnHeadOffice'],
    color: '#6a1b9a'
  }
];

function readTicketStatValue(stats, keys) {
  const value = keys.map((key) => stats?.[key]).find((item) => item !== undefined && item !== null);
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

/** Safe trim for nullable UI values (`null`/`undefined` → `''`). */
function trimmedString(value) {
  return String(value ?? '').trim();
}

function hasNonEmptyTrimmed(value) {
  return trimmedString(value) !== '';
}

function formatMerchantTicketPickLabel(merchant) {
  const vpa = hasNonEmptyTrimmed(merchant?.vpa) ? trimmedString(merchant.vpa) : '—';
  const phone = hasNonEmptyTrimmed(merchant?.phone) ? trimmedString(merchant.phone) : '—';
  return `${vpa} - ${phone}`;
}

function buildMerchantTicketSearchResultsById(searchResults) {
  return new Map(searchResults.map((row) => [row.id, row]));
}

function merchantTicketSearchHasActiveFilter(ticket, merchantResultsById) {
  const active = new Map([
    ['selectedId', Boolean(ticket.merchantTicketSelectedId)],
    ['hasResults', merchantResultsById.size > 0],
    ['hasSearchValue', hasNonEmptyTrimmed(ticket.merchantTicketSearchValue)]
  ]);
  return active.get('selectedId') || active.get('hasResults') || active.get('hasSearchValue');
}

function renderMerchantTicketPickDisplayValue(selected, merchantResultsById) {
  if (!selected) return null;
  const row = merchantResultsById.get(selected);
  return row ? formatMerchantTicketPickLabel(row) : selected;
}

function ticketDescriptionDisplayHtml(singleTicketData) {
  return (
    cleanTicketDescriptionText(singleTicketData?.description_text) ||
    singleTicketData?.description ||
    '---'
  );
}

function ticketConversationBodyHtml(msg) {
  return cleanTicketDescriptionText(msg.body_text) || msg.body;
}

function submitOnEnterKey(event, onSubmit) {
  if (event.key !== 'Enter') return;
  event.preventDefault();
  onSubmit();
}

function MerchantTicketMerchantPicker({
  merchantPickFloatingLabel,
  selectedId,
  searchResults,
  merchantResultsById,
  onSelectId
}) {
  const handleSelectChange = (event) => onSelectId(event.target.value);
  const renderPickValue = (selected) =>
    renderMerchantTicketPickDisplayValue(selected, merchantResultsById);

  return (
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel id="ticket-merchant-pick-label">{merchantPickFloatingLabel}</InputLabel>
      <Select
        labelId="ticket-merchant-pick-label"
        label={merchantPickFloatingLabel}
        value={selectedId || ''}
        onChange={handleSelectChange}
        displayEmpty
        renderValue={renderPickValue}
      >
        {searchResults.map((merchantRow) => (
          <MenuItem key={merchantRow.id} value={merchantRow.id}>
            {formatMerchantTicketPickLabel(merchantRow)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function MerchantTicketMerchantSearchField({ value, onChange, loading, onSubmit }) {
  const handleTextChange = (event) => onChange(event.target.value);
  const handleSearchClick = () => onSubmit();
  const handleKeyDown = (event) => submitOnEnterKey(event, onSubmit);

  const searchEndAdornment = loading ? (
    <CircularProgress color="inherit" size={22} />
  ) : (
    <IconButton
      onClick={handleSearchClick}
      aria-label="Search merchants for tickets"
      edge="end"
      size="small"
    >
      <SearchOutlinedIcon />
    </IconButton>
  );

  return (
    <TextField
      size="small"
      label="Enter Value"
      value={value}
      onChange={handleTextChange}
      disabled={loading}
      InputProps={{
        endAdornment: <InputAdornment position="end">{searchEndAdornment}</InputAdornment>
      }}
      onKeyDown={handleKeyDown}
    />
  );
}

function TicketExpandedDetailPanel({ loading, row, singleTicketData }) {
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4
        }}
      >
        <CircularProgress size={28} thickness={4} />
      </Box>
    );
  }

  const conversations = singleTicketData?.conversations;
  const shortSummary = singleTicketData?.custom_fields?.cf_short_summary;
  const hasConversationList = conversations?.length > 0;

  const shortSummarySection = shortSummary ? (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
      <Typography variant="subtitle2" sx={{ minWidth: '100px', fontWeight: 700 }}>
        Short Summary :{' '}
        <span style={{ fontWeight: 500 }}>
          {singleTicketData?.custom_fields?.cf_short_summary || '---'}
        </span>
      </Typography>
    </Box>
  ) : null;

  const conversationListSection = hasConversationList
    ? conversations.map((msg) => (
        <Box
          key={msg.id}
          sx={{
            borderBottom: '1px solid #eee',
            mb: 1,
            pb: 1
          }}
        >
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{ __html: ticketConversationBodyHtml(msg) }}
          />

          <Typography variant="caption" color="text.secondary">
            {isoDateFormatDmyhs(msg.created_at)}
          </Typography>
        </Box>
      ))
    : null;

  const conversationEmptySection = hasConversationList ? null : (
    <Typography variant="body2" color="text.secondary">
      No comments found.
    </Typography>
  );

  return (
    <>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        VPA :{'  '}
        <span style={{ fontWeight: 500 }}>{ticketVpaForExpanded(row, singleTicketData)}</span>
      </Typography>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Subject :{'  '}
        <span style={{ fontWeight: 500 }}>{singleTicketData?.subject || '---'}</span>
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ minWidth: '100px', fontWeight: 700 }}>
          Description :{' '}
        </Typography>

        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap' }}
          dangerouslySetInnerHTML={{ __html: ticketDescriptionDisplayHtml(singleTicketData) }}
        />
      </Box>

      {shortSummarySection}
      <Box sx={{ borderBottom: '1px solid #ddd', my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Mail Chain / Comments
      </Typography>

      {conversationListSection}
      {conversationEmptySection}
    </>
  );
}

function TicketTableRowGroup({ row, ticket }) {
  const isExpanded = ticket.expandedTicket === row?.id;
  const chipStyle = getTicketRowChipStyle(row?.status);

  const handleExpandToggle = () => {
    if (isExpanded) {
      ticket.expandedDetailFetchGenRef.current += 1;
      ticket.setExpandedTicket(null);
      ticket.setExpandedDetailLoading(false);
      ticket.setConversations({});
      ticket.setSingleTicketData({});
      return;
    }
    ticket.setExpandedTicket(row?.id);
    ticket.setConversations({});
    ticket.fetchTicketData(row?.id);
    ticket.fetchExpandedRowDetails(row);
  };

  const handleMoreMenu = (action) => ticket.handleTicketsMoreMenuClick(action, row);
  const expandToggleIcon = isExpanded ? <ExpandLess /> : <ExpandMore />;

  return (
    <>
      <TableRow
        hover
        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
        tabIndex={-1}
      >
        <TableCell>
          <IconButton onClick={handleExpandToggle}>{expandToggleIcon}</IconButton>
        </TableCell>
        <TableCell className="merchantTableListLabel">
          {rowFreshdeskDisplayId(row) || '---'}
        </TableCell>
        <TableCell className="merchantTableListLabel">{row?.ticketType || '---'}</TableCell>
        <TableCell className="merchantTableListLabel">{isoDateFormatDmyhs(row?.createdAt)}</TableCell>
        <TableCell className="merchantTableListLabel">{isoDateFormatDmyhs(row?.updatedAt)}</TableCell>
        <TableCell>
          <Chip
            label={row?.status}
            sx={{
              backgroundColor: chipStyle?.bg,
              color: chipStyle?.text,
              fontWeight: 600
            }}
            size="small"
          />
        </TableCell>
        <TableCell className="merchantTableListLabel">
          <TicketsMoreMenu handleUser={row} handleClicks={handleMoreMenu} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                p: 2,
                maxHeight: 400,
                overflowY: 'auto'
              }}
            >
              <TicketExpandedDetailPanel
                loading={ticket.expandedDetailLoading}
                row={row}
                singleTicketData={ticket.singleTicketData}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function TicketManagementCardContent() {
  const { sessionUserData } = useAuth();
  const ticketTableRef = useRef(null);
  const ticket = useTicketManagement(sessionUserData);
  const statsItems = ticketStatsConfig.map((item) => ({
    ...item,
    value: readTicketStatValue(ticket.ticketStats, item.keys)
  }));

  const merchantPickFloatingLabel = trimmedString(ticket.merchantTicketSearchValue) || 'Merchant';

  const merchantTicketSearchResultsById = useMemo(
    () => buildMerchantTicketSearchResultsById(ticket.merchantTicketSearchResults),
    [ticket.merchantTicketSearchResults]
  );

  const hasTicketList = ticket.ticketListData != null;
  const hasMerchantTicketSearchActive = merchantTicketSearchHasActiveFilter(
    ticket,
    merchantTicketSearchResultsById
  );
  const dateFilterButtonLabel = ticket.customDateSelectedString || 'Filter by date';
  const addTicketModalKey = ticket.selectedTicketForAction?.ticket?.id
    ? `update-${ticket.selectedTicketForAction.ticket.id}`
    : 'create-ticket';
  const showTicketListEmptyRow =
    ticket.ticketListData?.length === 0 && ticket.ticketListLoading !== true;
  const hasTicketDateFilter = Boolean(ticket.customDateSelectedString);

  const openCreateTicket = () => ticket.setCreateTicketOpen(true);
  const handleTicketSearchTextChange = (event) => ticket.setSearchText(event.target.value);
  const handleTicketIdSearchKeyDown = (event) => submitOnEnterKey(event, ticket.handleTicketSearchSubmit);
  const handleMerchantTicketSearchTypeChange = (event) =>
    ticket.handleMerchantTicketSearchTypeChange(event.target.value);
  const clearMerchantTicketSearch = () => ticket.handleClearMerchantTicketSearch();
  const openDateRangeDialog = () => ticket.setIsDateRangeDialogOpen(true);
  const clearTicketDateFilter = () => ticket.onClearTicketDateFilter();
  const closeSnackBar = () => ticket.setIsSnackBarAlertOpen(false);
  const closeDateRangeDialog = () => ticket.setIsDateRangeDialogOpen(false);
  const applyTicketDateRange = (fromDate, toDate) =>
    ticket.onTicketDateRangeSelected(fromDate, toDate);
  const closeAddTicketModal = () => ticket.setCreateTicketOpen(false);
  const goToNextTicketPage = () => ticket.handleChangePage('NEXT');

  const showMerchantTicketPicker = ticket.merchantTicketShowMerchantPicker;
  const merchantTicketValueControl = showMerchantTicketPicker ? (
    <MerchantTicketMerchantPicker
      merchantPickFloatingLabel={merchantPickFloatingLabel}
      selectedId={ticket.merchantTicketSelectedId}
      searchResults={ticket.merchantTicketSearchResults}
      merchantResultsById={merchantTicketSearchResultsById}
      onSelectId={ticket.handleMerchantTicketSelectId}
    />
  ) : (
    <MerchantTicketMerchantSearchField
      value={ticket.merchantTicketSearchValue}
      onChange={ticket.setMerchantTicketSearchValue}
      loading={ticket.merchantTicketSearchLoading}
      onSubmit={ticket.handleMerchantTicketSearchSubmit}
    />
  );

  const merchantSearchClearButton = hasMerchantTicketSearchActive ? (
    <IconButton
      size="small"
      color="error"
      onClick={clearMerchantTicketSearch}
      aria-label="Clear merchant search"
    >
      <CloseOutlinedIcon />
    </IconButton>
  ) : null;

  const ticketDateFilterClearButton = hasTicketDateFilter ? (
    <IconButton
      size="small"
      color="error"
      onClick={clearTicketDateFilter}
      aria-label="Clear ticket date filter"
    >
      <CloseOutlinedIcon />
    </IconButton>
  ) : null;

  const ticketListLoadingRow = ticket.ticketListLoading ? (
    <TableRow>
      <TableCell colSpan={7} align="center">
        <CircularProgress size={40} />
      </TableCell>
    </TableRow>
  ) : null;

  const ticketListRowGroups = ticket.ticketListData?.map((row) => (
    <TicketTableRowGroup key={row?.id} row={row} ticket={ticket} />
  ));

  const ticketListEmptyRow = showTicketListEmptyRow ? (
    <TableRow>
      <TableCell colSpan={7}>
        <NoDataAvailable />
      </TableCell>
    </TableRow>
  ) : null;

  const ticketListTableSection = hasTicketList ? (
    <>
      <TableContainer
        sx={{ maxHeight: '60vh', minHeight: '60vh', overflowY: 'scroll' }}
        ref={ticketTableRef}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 900 }}>Ticket-ID</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Complaint Type</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Updated At</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ticketListLoadingRow}
            {ticketListRowGroups}
            {ticketListEmptyRow}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        page={ticket.page}
        handleNextPage={goToNextTicketPage}
        handlePreviousPage={ticket.handleChangePage}
        hasNextPage={ticket.hasNextPage}
        data={ticket.ticketListData}
        setPage={ticket.setPage}
      />
    </>
  ) : null;

  const confirmDialog = ticket.dialogConfig;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2
        }}
      >
        <Button
          variant="contained"
          color="whitebg"
          size="small"
          sx={{ minHeight: '5vh', minWidth: 120 }}
          onClick={openCreateTicket}
          startIcon={<AddIcon />}
        >
          Create Ticket
        </Button>
      </Box>

      <Card
        elevation={0}
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 1,
          border: '1px solid #E6EAF2',
          boxShadow: '0px 8px 24px rgba(0,0,0,0.06)'
        }}
      >
        <Grid container spacing={1.5}>
          {statsItems.map((item) => (
            <Grid
              size={{ xs: 3, sm: 4, md: 1.7 }}
              key={item.label}
              sx={{
                p: 1.25,
                borderRadius: 1,
                border: '1px solid #EDF0F5',
                bgcolor: '#FFFFFF'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5,
                  flexDirection: 'column'
                }}
              >
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#171D40' }}>
                  {item.value}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: item?.color }}>
                  {item.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Card>

      <Card>
        <Stack
          direction={{ md: 'row', xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ p: 2 }}
          mt={1}
          mb={1}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap' }}>
                Filter By
              </Typography>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="device-select-label">Merchant Status</InputLabel>
              <Select
                labelId="device-select-label"
                id="device-select"
                value={ticket.status}
                onChange={ticket.handleStatusClickChange}
                label="Merchant Status"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="Resolved">Resolved</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Enter Ticket ID"
              value={ticket.searchText}
              onChange={handleTicketSearchTextChange}
              InputProps={{
                inputProps: {
                  'data-testid': 'searchText'
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={ticket.handleTicketSearchSubmit}
                      aria-label="Search tickets"
                    >
                      <SearchOutlinedIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              onKeyDown={handleTicketIdSearchKeyDown}
            />
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
            spacing={1}
            flexWrap="wrap"
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel id="ticket-merchant-search-type-label">Search By</InputLabel>
                <Select
                  labelId="ticket-merchant-search-type-label"
                  value={ticket.merchantTicketSearchType}
                  onChange={handleMerchantTicketSearchTypeChange}
                  label="Search By"
                >
                  <MenuItem value="name">Merchant Name</MenuItem>
                  <MenuItem value="vpa">Merchant VPA</MenuItem>
                  <MenuItem value="phone">Merchant Phone No.</MenuItem>
                </Select>
              </FormControl>

              <Typography sx={{ fontWeight: 700 }}>=</Typography>

              {merchantTicketValueControl}

              {merchantSearchClearButton}
            </Stack>

            <Button
              variant="outlined"
              size="small"
              startIcon={<CalendarMonthOutlinedIcon />}
              onClick={openDateRangeDialog}
              sx={{
                minHeight: 40,
                borderRadius: 1,
                textTransform: 'none',
                whiteSpace: 'nowrap'
              }}
            >
              {dateFilterButtonLabel}
            </Button>
            {ticketDateFilterClearButton}
          </Stack>
        </Stack>

        <Divider sx={{ mx: 1 }} />

        {ticketListTableSection}
      </Card>

      <SnackBarAlert
        open={ticket.isSnackBarAlertOpen}
        title={ticket.snackBarMetas?.title}
        message={ticket.snackBarMetas?.message}
        severity={ticket.snackBarMetas?.severity}
        handleClose={closeSnackBar}
      />

      <ConfirmDialog
        open={ticket.openConfirm}
        message={confirmDialog?.message}
        onCancel={confirmDialog?.onCancel}
        onConfirm={confirmDialog?.onConfirm}
        loading={confirmDialog?.loading}
        note={confirmDialog?.note}
        confirmText={confirmDialog?.confirmText}
      />

      <DateRangePickerDialog
        openDateRangeModal={ticket.isDateRangeDialogOpen}
        handleClose={closeDateRangeDialog}
        onAction={applyTicketDateRange}
      />

      <AddTicketModal
        key={addTicketModalKey}
        open={ticket.createTicketOpen}
        handleClose={closeAddTicketModal}
        ticketData={ticket.selectedTicketForAction}
        setTicketData={ticket.setSelectedTicketForAction}
        onReload={ticket?.onReload}
      />
    </>
  );
}
